import './styles.scss';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import { INLINES, BLOCKS } from '@contentful/rich-text-types';
import { Collapsible } from '@edx/paragon';

import { updateAccordions } from '@data/actions/accordions';
import { submitEvent } from '@utils/analytics';
import { formatEmbeddedEntries } from '@utils/contentfulFormatting';
import { formatRichText } from '@utils/formatting';
import FAQItemProptypes from './propTypes';

const formatContent = (text, references, footnoteMap) => {
  const options = {
    renderNode: {
      // eslint-disable-next-line react/prop-types
      [INLINES.HYPERLINK]: ({ data }, children) => (
        <a
        // eslint-disable-next-line react/prop-types
          href={data.uri}
          target="_blank"
          rel="noreferrer"
        >{children}
        </a>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <h4 className="h2 my-4">
          {children}
        </h4>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <h5 className="h3 my-4">
          {children}
        </h5>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <h6 className="h4 my-4">
          {children}
        </h6>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul>{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol>{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
      ...formatEmbeddedEntries(references, footnoteMap),
      [BLOCKS.PARAGRAPH]: (node, children) => <p style={{ whiteSpace: 'pre-line' }}>{children}</p>,
    },
  };

  return formatRichText(text, options);
};

function FAQs({
  footnoteMap,
  questions,
  tracking,
  faqStructuredData,
  pageTitle,
  isFlex,
}) {
  const dispatch = useDispatch();
  const accordions = useSelector((state) => state.accordions);

  const handleFaqClick = (isOpen, id) => {
    const componentProps = {
      category: 'navigation',
      isOpen,
      page_title: pageTitle,
      is_flex: isFlex,
    };

    submitEvent(tracking, componentProps, 'edx.bi.faq.clicked');
    dispatch(updateAccordions({ id, isOpen }));
  };
  if (questions && questions.length > 0) {
    const inner = questions.map(({
      id,
      question,
      answer,
      answerRichText,
    }) => {
      const includeFaqAsMicroData = faqStructuredData.find(item => item === id);
      const openCollapsible = accordions[id];
      const answerText = (answerRichText?.raw
        ? formatContent(answerRichText.raw, answerRichText.references, footnoteMap)
        : answer);

      if (includeFaqAsMicroData) {
        return (
          <li className="list-item pb-3.5" key={id} data-testid="faq-item">
            <Collapsible
              title={<h3 className="faq-question mb-0">{question}</h3>}
              onToggle={(open) => handleFaqClick(open, id)}
            >
              <div
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <div className="d-none">
                  <h3 itemProp="name">{question}</h3>
                </div>
                <div
                  data-testid="faq-answer"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <div itemProp="text">{answerText}</div>
                </div>
              </div>
            </Collapsible>
          </li>
        );
      }
      const sectionId = question.replace(/\s+/g, '-').toLowerCase();
      return (
        <li className="list-item pb-3.5" id={sectionId} key={id} data-testid="faq-item">
          <Collapsible
            title={<h3 className="faq-question mb-0">{question}</h3>}
            onToggle={(open) => handleFaqClick(open, id)}
            open={openCollapsible}
          >
            <div data-testid="faq-answer">{answerText}</div>
          </Collapsible>
        </li>
      );
    });
    return (
      <ul className="p-0 list-unstyled list-group list-group-flush">
        {inner}
      </ul>
    );
  }

  return null;
}

FAQs.propTypes = {
  footnoteMap: PropTypes.objectOf(PropTypes.number),
  questions: PropTypes.arrayOf(PropTypes.shape(FAQItemProptypes)),
  tracking: PropTypes.shape(),
  faqStructuredData: PropTypes.arrayOf(PropTypes.string),
  pageTitle: PropTypes.string,
  isFlex: PropTypes.bool,
};

FAQs.defaultProps = {
  footnoteMap: {},
  questions: [],
  tracking: {},
  faqStructuredData: [],
  pageTitle: '',
  isFlex: false,
};

export default track({
  component: 'faqs',
})(FAQs);
